import { LitElement, html, css, svg } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <cos-icon> — Lucide-style outline icon at a fixed nominal size.
 *
 * Stage 19f: replaces the unicode-character icons in the sidebar nav
 * (audit issue 033 — `◴ ✦ ▣ ◐ ❒` was a mix of hand-drawn shapes with
 * no coherent family). Inline-SVG approach to avoid pulling the full
 * lucide package: ~0.4KB for the six icons we need vs ~150KB
 * tree-shaken-or-not for the npm dep.
 *
 * Path data lifted from lucide.dev (MIT). Extending the icon set is
 * a one-line entry in ICONS — no consumer changes.
 */

const ICONS: Record<string, ReturnType<typeof svg>> = {
	// Desk board: "Full board" rail button (lucide "layout-grid").
	"layout-grid": svg`
		<rect width="7" height="7" x="3" y="3" rx="1"></rect>
		<rect width="7" height="7" x="14" y="3" rx="1"></rect>
		<rect width="7" height="7" x="14" y="14" rx="1"></rect>
		<rect width="7" height="7" x="3" y="14" rx="1"></rect>`,
	// Lend (Library of Things) nav entry (lucide "package").
	package: svg`
		<path d="M16.5 9.4 7.55 4.24"></path>
		<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path>
		<polyline points="3.29 7 12 12 20.71 7"></polyline>
		<line x1="12" y1="22" x2="12" y2="12"></line>`,
	// Circulate staff UI — column-filter affordance (lucide "search").
	search: svg`
		<circle cx="11" cy="11" r="8"></circle>
		<line x1="21" y1="21" x2="16.65" y2="16.65"></line>`,
	// Circulate staff UI — print report action (feather "printer").
	printer: svg`
		<polyline points="6 9 6 2 18 2 18 9"></polyline>
		<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
		<rect x="6" y="14" width="12" height="8"></rect>`,
	// Stage 61b — the /mail inbox nav entry (feather "mail").
	mail: svg`
		<rect x="2" y="4" width="20" height="16" rx="2"></rect>
		<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>`,
	// Stage 73 — CSV download action (feather "download").
	download: svg`
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7 10 12 15 17 10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>`,
	// Stage 76 — the inbox nav entry (feather "inbox").
	inbox: svg`
		<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
		<path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>`,
	dashboard: svg`
		<rect x="3" y="3" width="7" height="9"></rect>
		<rect x="14" y="3" width="7" height="5"></rect>
		<rect x="14" y="12" width="7" height="9"></rect>
		<rect x="3" y="16" width="7" height="5"></rect>`,
	// Measure Stage 1 — the analytics nav entry (Lucide "bar-chart-3").
	chart: svg`
		<path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
		<path d="M18 17V9"></path>
		<path d="M13 17V5"></path>
		<path d="M8 17v-3"></path>`,
	// Nurture Stage 1 — the CRM nav entry (Lucide "heart").
	heart: svg`
		<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.51 4.04 3 5.5l7 7Z"></path>`,
	// Broadcast Stage B0 — the digital-signage nav entry (Lucide "monitor").
	monitor: svg`
		<rect width="20" height="14" x="2" y="3" rx="2"></rect>
		<line x1="8" x2="16" y1="21" y2="21"></line>
		<line x1="12" x2="12" y1="17" y2="21"></line>`,
	ticket: svg`
		<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
		<path d="M13 5v2"></path>
		<path d="M13 17v2"></path>
		<path d="M13 11v2"></path>`,
	folder: svg`
		<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path>`,
	// Stage 92a — Files panel glyphs (Lucide). file/folder for rows;
	// upload/share/trash for the top + per-row actions (92b/c enable
	// upload/share/trash; 92a renders them disabled-placeholding).
	file: svg`
		<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
		<polyline points="14 2 14 8 20 8"></polyline>`,
	// Stage 92c — /files nav entry (Lucide "folder-open"; distinct from
	// Projects' "folder").
	'folder-open': svg`
		<path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"></path>`,
	upload: svg`
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17 8 12 3 7 8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>`,
	share: svg`
		<circle cx="18" cy="5" r="3"></circle>
		<circle cx="6" cy="12" r="3"></circle>
		<circle cx="18" cy="19" r="3"></circle>
		<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
		<line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>`,
	trash: svg`
		<polyline points="3 6 5 6 21 6"></polyline>
		<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>`,
	users: svg`
		<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
		<circle cx="9" cy="7" r="4"></circle>
		<path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
		<path d="M16 3.13a4 4 0 0 1 0 7.75"></path>`,
	"book-open": svg`
		<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
		<path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>`,
		// Training's nav entry (Lucide "graduation-cap". MIT). Distinct from
		// Circulate's "book-open" so the two staff-surface entries don't
		// share a glyph.
		"graduation-cap": svg`
			<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"></path>
			<path d="M22 10v6"></path>
			<path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"></path>`,
	// The Audiences builder's Rooms filter group (Lucide 'door-open'. MIT).
	"door-open": svg`
		<path d="M13 4h3a2 2 0 0 1 2 2v14"></path>
		<path d="M2 20h3"></path>
		<path d="M13 20h9"></path>
		<path d="M10 12v.01"></path>
		<path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561z"></path>`,
	// The Audiences builder's patron-bucket group (Lucide 'compass'. MIT).
	compass: svg`
		<path d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"></path>
		<circle cx="12" cy="12" r="10"></circle>`,
	// Preserve (digital archives) nav entry. Lucide 'archive'. MIT.
	archive: svg`
		<rect x="2" y="3" width="20" height="5" rx="1"></rect>
		<path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path>
		<path d="M10 12h4"></path>`,
	"clipboard-list": svg`
		<rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
		<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
		<path d="M12 11h4"></path>
		<path d="M12 16h4"></path>
		<path d="M8 11h.01"></path>
		<path d="M8 16h.01"></path>`,
	// 63d polish — edit-mode grid chrome. Lucide `move` (4-way arrows;
	// Paul: "the move icon needs to be bolder and like 4 arrows") and
	// `move-vertical` (the centered height handle). MIT.
	move: svg`
		<polyline points="5 9 2 12 5 15"></polyline>
		<polyline points="9 5 12 2 15 5"></polyline>
		<polyline points="15 19 12 22 9 19"></polyline>
		<polyline points="19 9 22 12 19 15"></polyline>
		<line x1="2" y1="12" x2="22" y2="12"></line>
		<line x1="12" y1="2" x2="12" y2="22"></line>`,
	"move-vertical": svg`
		<polyline points="8 18 12 22 16 18"></polyline>
		<polyline points="8 6 12 2 16 6"></polyline>
		<line x1="12" y1="2" x2="12" y2="22"></line>`,
	"log-out": svg`
		<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
		<polyline points="16 17 21 12 16 7"></polyline>
		<line x1="21" y1="12" x2="9" y2="12"></line>`,
	// Stage 20 — admin nav entry. Lucide `shield` (MIT).
	shield: svg`
		<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>`,
	// Stage 85g — mail reply/forward + attach affordances (replacing emoji).
	// Lucide `corner-up-left` + `paperclip` (MIT).
	reply: svg`
		<polyline points="9 14 4 9 9 4"></polyline>
		<path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>`,
	// Inbound email v2, Stage 1 — the compose panel's Forward action.
	// Lucide `corner-up-right` (MIT) — the mirror of "reply" above.
	forward: svg`
		<polyline points="15 14 20 9 15 4"></polyline>
		<path d="M4 20v-7a4 4 0 0 1 4-4h12"></path>`,
	paperclip: svg`
		<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>`,
	// Stage 85h — chat "add reaction" affordance (replacing the 🙂 emoji).
	// Lucide `smile-plus` (MIT).
	"smile-plus": svg`
		<path d="M22 11v1a10 10 0 1 1-9-10"></path>
		<path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
		<line x1="9" y1="9" x2="9.01" y2="9"></line>
		<line x1="15" y1="9" x2="15.01" y2="9"></line>
		<path d="M16 5h6"></path>
		<path d="M19 2v6"></path>`,
	// Stage 85a — user dropdown "Settings" entry. Lucide `settings` (MIT).
	settings: svg`
		<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
		<circle cx="12" cy="12" r="3"></circle>`,
	// Stage 30 — admin agent FAB. Lucide `message-circle` (MIT).
	"message-circle": svg`
		<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>`,
	// Stage 34a — needs_attention toggle on ticket detail. Lucide
	// `flag` (MIT). The component is render-stroked-only (fill: none
	// in the static styles); the "flagged" state inherits color from
	// the parent button class (red).
	flag: svg`
		<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
		<line x1="4" y1="22" x2="4" y2="15"></line>`,
	// Marcie Stage 3 — pin-a-note affordance. Lucide \`pin\` (MIT).
	pin: svg`
		<line x1="12" y1="17" x2="12" y2="22"></line>
		<path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>`,
	// Collections folders/favorites (2026-08) — per-user star affordance
	// (never a raw ★/☆ glyph). Lucide `star` (MIT).
	star: svg`
		<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>`,
	// Journey canvas v2 (2026-08) — the step library's plain-language
	// cards. Lucide `zap` (something happened), `tag` (staff applied a
	// tag), `moon` (went quiet), `git-branch` (the branch step). All MIT.
	zap: svg`
		<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>`,
	tag: svg`
		<path d="M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.41l8.7 8.7a2 2 0 0 0 2.83 0l7.17-7.17a2 2 0 0 0 0-2.83z"></path>
		<line x1="7" y1="7" x2="7.01" y2="7"></line>`,
	moon: svg`
		<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>`,
	"git-branch": svg`
		<line x1="6" y1="3" x2="6" y2="15"></line>
		<circle cx="18" cy="6" r="3"></circle>
		<circle cx="6" cy="18" r="3"></circle>
		<path d="M18 9a9 9 0 0 1-9 9"></path>`,
	// Partner map view (2026-08) — the /partners "Map" header-action button
	// (lucide "map").
	map: svg`
		<polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6"></polygon>
		<line x1="9" y1="3" x2="9" y2="18"></line>
		<line x1="15" y1="6" x2="15" y2="21"></line>`,
	// Stage 34a — github_branch copy button on engineering sidebar.
	// Lucide `copy` (MIT).
	copy: svg`
		<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
		<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>`,
	// Stage 35b — Deals nav entry. Lucide `dollar-sign` (MIT). Reads
	// as money/sales without leaning on a brand mark; the same icon
	// family as the rest of the sidebar.
	"dollar-sign": svg`
		<line x1="12" y1="1" x2="12" y2="23"></line>
		<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>`,
	// Stage 36b — completeness-alert banner on cos-partner-detail.
	// Lucide `alert-triangle` (MIT). Used inline with banner copy so
	// CSMs see the warning shape before reading the text.
	"alert-triangle": svg`
		<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
		<line x1="12" y1="9" x2="12" y2="13"></line>
		<line x1="12" y1="17" x2="12.01" y2="17"></line>`,
	// Stage 38a — notification bell in cos-app-shell header.
	// Lucide `bell` (MIT). Adjacent amber-tinted badge shows
	// unread count when > 0.
	bell: svg`
		<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
		<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>`,
	// Stage 84 — muted-conversation watch state. Lucide `bell-off` (MIT).
	"bell-off": svg`
		<path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5"></path>
		<path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7"></path>
		<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
		<path d="m2 2 20 20"></path>`,
	// Stage 39b — Control Panel deep-link affordance on the
	// Partner Overview Card. Lucide `external-link` (MIT).
	"external-link": svg`
		<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
		<polyline points="15 3 21 3 21 9"></polyline>
		<line x1="10" y1="14" x2="21" y2="3"></line>`,
	// Stage 41a — KB callout admonitions. Lucide `info`,
	// `lightbulb`, `alert-octagon` (MIT). `alert-triangle`
	// already exists above (Stage 36b completeness banner).
	info: svg`
		<circle cx="12" cy="12" r="10"></circle>
		<line x1="12" y1="16" x2="12" y2="12"></line>
		<line x1="12" y1="8" x2="12.01" y2="8"></line>`,
	// The Boardroom rail entry (lucide "landmark"). A civic columned
	// building — the icon set had nothing for a governance body, and the
	// nearest existing glyphs read wrong: "users" is any group of people,
	// "clipboard-list" is the agenda rather than the board.
	landmark: svg`
		<line x1="3" y1="22" x2="21" y2="22"></line>
		<line x1="6" y1="18" x2="6" y2="11"></line>
		<line x1="10" y1="18" x2="10" y2="11"></line>
		<line x1="14" y1="18" x2="14" y2="11"></line>
		<line x1="18" y1="18" x2="18" y2="11"></line>
		<polygon points="12 2 20 7 4 7"></polygon>`,
	// The /billing/customers nav entry (lucide "building") — a customer is
	// an institution, not a document, distinguishing it from Contracts'
	// 'file' and Partners' 'users'.
	building: svg`
		<rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
		<path d="M9 22v-4h6v4"></path>
		<path d="M8 6h.01"></path>
		<path d="M12 6h.01"></path>
		<path d="M16 6h.01"></path>
		<path d="M8 10h.01"></path>
		<path d="M12 10h.01"></path>
		<path d="M16 10h.01"></path>
		<path d="M8 14h.01"></path>
		<path d="M12 14h.01"></path>
		<path d="M16 14h.01"></path>`,
	lightbulb: svg`
		<path d="M9 18h6"></path>
		<path d="M10 22h4"></path>
		<path d="M2 9a7 7 0 0 1 14 0c0 3-2 5-2 6H4c0-1-2-3-2-6z"></path>`,
	"alert-octagon": svg`
		<polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
		<line x1="12" y1="8" x2="12" y2="12"></line>
		<line x1="12" y1="16" x2="12.01" y2="16"></line>`,
	// Stage 41a — feedback footer thumbs-up / thumbs-down.
	"thumbs-up": svg`
		<path d="M7 10v12"></path>
		<path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L15 2v0a3 3 0 0 1 3 3v0c0 .29-.07.58-.18.85L15 5.88z"></path>`,
	"thumbs-down": svg`
		<path d="M17 14V2"></path>
		<path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L9 22v0a3 3 0 0 1-3-3v0c0-.29.07-.58.18-.85L9 18.12z"></path>`,
	// Stage 41a.1 — KB article metadata + nav affordances.
	// All Lucide (MIT) — clock, user, calendar, eye, arrow-left,
	// chevron-down.
	clock: svg`
		<circle cx="12" cy="12" r="10"></circle>
		<polyline points="12 6 12 12 16 14"></polyline>`,
	user: svg`
		<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
		<circle cx="12" cy="7" r="4"></circle>`,
	// Stage 42e — sidebar "My work" entry. Lucide user-check
	// (MIT) — same silhouette as `user` with a small check
	// glyph to its upper-right.
	"user-check": svg`
		<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
		<circle cx="9" cy="7" r="4"></circle>
		<polyline points="16 11 18 13 22 9"></polyline>`,
	// Desk — "Replace a lost card" task. Lucide credit-card (MIT).
	"credit-card": svg`
		<rect width="20" height="14" x="2" y="5" rx="2"></rect>
		<line x1="2" x2="22" y1="10" y2="10"></line>`,
	calendar: svg`
		<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
		<line x1="16" y1="2" x2="16" y2="6"></line>
		<line x1="8" y1="2" x2="8" y2="6"></line>
		<line x1="3" y1="10" x2="21" y2="10"></line>`,
	eye: svg`
		<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
		<circle cx="12" cy="12" r="3"></circle>`,
	"arrow-left": svg`
		<line x1="19" y1="12" x2="5" y2="12"></line>
		<polyline points="12 19 5 12 12 5"></polyline>`,
	"chevron-down": svg`
		<polyline points="6 9 12 15 18 9"></polyline>`,
	// Disclosure-button "collapsed" twisty (app-shell module flyout,
	// partner-admin accordion nav). Lucide "chevron-right" (MIT).
	"chevron-right": svg`
		<polyline points="9 18 15 12 9 6"></polyline>`,
	// Reserve day-navigator "previous day" arrow. Lucide "chevron-left" (MIT).
	"chevron-left": svg`
		<polyline points="15 18 9 12 15 6"></polyline>`,
	// Stream 4 — sortable-column header indicators. chevron-up/-down show the
	// ACTIVE sort direction; chevrons-up-down is the neutral "this column is
	// sortable" affordance. Lucide (MIT).
	"chevron-up": svg`
		<polyline points="18 15 12 9 6 15"></polyline>`,
	"chevrons-up-down": svg`
		<polyline points="7 15 12 20 17 15"></polyline>
		<polyline points="7 9 12 4 17 9"></polyline>`,
	// Stage 41b-2 — KB editor toolbar icons. All Lucide MIT.
	"heading-2": svg`
		<path d="M4 12h8"></path>
		<path d="M4 18V6"></path>
		<path d="M12 18V6"></path>
		<path d="M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1"></path>`,
	"heading-3": svg`
		<path d="M4 12h8"></path>
		<path d="M4 18V6"></path>
		<path d="M12 18V6"></path>
		<path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2"></path>
		<path d="M17 17.5c2 1.5 5 .3 5-2.5a2 2 0 0 0-2-2"></path>`,
	list: svg`
		<line x1="8" y1="6" x2="21" y2="6"></line>
		<line x1="8" y1="12" x2="21" y2="12"></line>
		<line x1="8" y1="18" x2="21" y2="18"></line>
		<line x1="3" y1="6" x2="3.01" y2="6"></line>
		<line x1="3" y1="12" x2="3.01" y2="12"></line>
		<line x1="3" y1="18" x2="3.01" y2="18"></line>`,
	"list-ordered": svg`
		<line x1="10" y1="6" x2="21" y2="6"></line>
		<line x1="10" y1="12" x2="21" y2="12"></line>
		<line x1="10" y1="18" x2="21" y2="18"></line>
		<path d="M4 6h1v4"></path>
		<path d="M4 10h2"></path>
		<path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path>`,
	quote: svg`
		<path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path>
		<path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"></path>`,
	bold: svg`
		<path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
		<path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>`,
	italic: svg`
		<line x1="19" y1="4" x2="10" y2="4"></line>
		<line x1="14" y1="20" x2="5" y2="20"></line>
		<line x1="15" y1="4" x2="9" y2="20"></line>`,
	code: svg`
		<polyline points="16 18 22 12 16 6"></polyline>
		<polyline points="8 6 2 12 8 18"></polyline>`,
	link: svg`
		<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
		<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>`,
	"code-block": svg`
		<rect x="3" y="3" width="18" height="18" rx="2"></rect>
		<polyline points="10 14 8 12 10 10"></polyline>
		<polyline points="14 10 16 12 14 14"></polyline>`,
	table: svg`
		<path d="M12 3v18"></path>
		<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
		<path d="M3 9h18"></path>
		<path d="M3 15h18"></path>`,
	// Reports (data-extract) builder nav entry. Distinct from `table`
	// (already the Stage-48 canned-dashboard-reports icon) and `download`
	// (a generic action) — this needs to read as its own thing. Lucide
	// "file-spreadsheet" (MIT).
	"file-spreadsheet": svg`
		<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
		<polyline points="14 2 14 8 20 8"></polyline>
		<path d="M8 13h8"></path>
		<path d="M8 17h8"></path>
		<path d="M8 13v4"></path>
		<path d="M12 13v4"></path>
		<path d="M16 13v4"></path>`,
	// Partner round 1 (Marcie) B2 — the /partners advanced-filter builder
	// affordance (lucide "filter"). B2 shipped referencing this name before the
	// glyph existed; an unknown name renders the empty box below, so the button
	// silently had no icon until B3 added it.
	filter: svg`
		<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>`,
	// Stage 41b-3 — KB editor upload + autocomplete affordances.
	image: svg`
		<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
		<circle cx="8.5" cy="8.5" r="1.5"></circle>
		<polyline points="21 15 16 10 5 21"></polyline>`,
	// Stage 46c — section primitive chrome (drag handle, inline edit,
	// lane switch, hide tray, edit-mode banner controls). Lucide (MIT).
	"grip-vertical": svg`
		<circle cx="9" cy="12" r="1"></circle>
		<circle cx="9" cy="5" r="1"></circle>
		<circle cx="9" cy="19" r="1"></circle>
		<circle cx="15" cy="12" r="1"></circle>
		<circle cx="15" cy="5" r="1"></circle>
		<circle cx="15" cy="19" r="1"></circle>`,
	pencil: svg`
		<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
		<path d="m15 5 4 4"></path>`,
	"arrow-right": svg`
		<path d="M5 12h14"></path>
		<path d="m12 5 7 7-7 7"></path>`,
	"arrow-left-right": svg`
		<path d="M8 3 4 7l4 4"></path>
		<path d="M4 7h16"></path>
		<path d="m16 21 4-4-4-4"></path>
		<path d="M20 17H4"></path>`,
	x: svg`
		<path d="M18 6 6 18"></path>
		<path d="m6 6 12 12"></path>`,
	plus: svg`
		<path d="M5 12h14"></path>
		<path d="M12 5v14"></path>`,
	check: svg`
		<path d="M20 6 9 17l-5-5"></path>`,
	// Big Arc Sub-arc 1 — the /tasks nav entry (lucide "check-square").
	// Was referenced by STAFF_NAV before this glyph existed, so the
	// unknown-name fallback silently rendered an empty box (reported bug).
	"check-square": svg`
		<polyline points="9 11 12 14 22 4"></polyline>
		<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>`,
	"rotate-ccw": svg`
		<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
		<path d="M3 3v5h5"></path>`,
	"eye-off": svg`
		<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
		<path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
		<path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
		<line x1="2" x2="22" y1="2" y2="22"></line>`,
	// Feedback capture launcher (lucide "bug").
	bug: svg`
		<path d="m8 2 1.88 1.88"></path>
		<path d="M14.12 3.88 16 2"></path>
		<path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path>
		<path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path>
		<path d="M12 20v-9"></path>
		<path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path>
		<path d="M6 13H2"></path>
		<path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path>
		<path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path>
		<path d="M22 13h-4"></path>
		<path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path>`,
	// Feedback annotator — Rectangle tool (lucide "square").
	square: svg`
		<rect x="3" y="3" width="18" height="18" rx="2"></rect>`,
	// Feedback annotator — Pen tool (lucide "pen-line").
	"pen-tool": svg`
		<path d="M12 20h9"></path>
		<path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"></path>`,
	// Feedback annotator — Text tool (lucide "type").
	type: svg`
		<polyline points="4 7 4 4 20 4 20 7"></polyline>
		<line x1="9" y1="20" x2="15" y2="20"></line>
		<line x1="12" y1="4" x2="12" y2="20"></line>`,
	// Feedback annotator — Blur/redact tool (a droplet — closest stock
	// glyph to "obscure this region").
	droplet: svg`
		<path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"></path>`,
	// Feedback panel — Screenshot capture button (lucide "camera").
	camera: svg`
		<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
		<circle cx="12" cy="13" r="3"></circle>`,
	// Feedback panel — Record capture button (lucide "video").
	video: svg`
		<path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"></path>
		<rect x="2" y="6" width="14" height="12" rx="2"></rect>`,
	// Feedback panel — "Point at the problem" element picker (lucide "crosshair").
	crosshair: svg`
		<circle cx="12" cy="12" r="10"></circle>
		<line x1="22" y1="12" x2="18" y2="12"></line>
		<line x1="6" y1="12" x2="2" y2="12"></line>
		<line x1="12" y1="6" x2="12" y2="2"></line>
		<line x1="12" y1="22" x2="12" y2="18"></line>`,
	// Email Studio — the divider block (lucide "minus").
	minus: svg`
		<path d="M5 12h14"></path>`,
	// Email Studio — the columns block (lucide "columns-2").
	columns: svg`
		<rect width="18" height="18" x="3" y="3" rx="2"></rect>
		<path d="M12 3v18"></path>`,
	// Email Studio stage 3 — the collection_carousel block (lucide "gallery-horizontal").
	"gallery-horizontal": svg`
		<path d="M2 3v18"></path>
		<rect width="12" height="18" x="6" y="3" rx="2"></rect>
		<path d="M22 3v18"></path>`,
	// Email Studio v2 — the image_group block (lucide "images").
	images: svg`
		<path d="M18 22H4a2 2 0 0 1-2-2V6"></path>
		<path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18"></path>
		<circle cx="12" cy="8" r="2"></circle>
		<rect width="16" height="16" x="6" y="2" rx="2"></rect>`,
	// Email Studio v2 — the image_card block (lucide "layout-template").
	"layout-template": svg`
		<rect width="18" height="7" x="3" y="3" rx="1"></rect>
		<rect width="9" height="7" x="3" y="14" rx="1"></rect>
		<rect width="5" height="7" x="16" y="14" rx="1"></rect>`,
	// Nurture's Marketing rail entry (lucide "megaphone"). Its sibling
	// Audience keeps `heart`, the glyph the single Nurture entry carried
	// before the split, so an existing rail keeps a familiar landmark.
	megaphone: svg`
		<path d="m3 11 18-5v12L3 14v-3z"></path>
		<path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>`,
	// Surveys' own rail entry (lucide "list-checks"), promoted out of
	// Marketing's tab set (Paul, 2026-08-20). Distinct from the plain
	// `list` glyph above — the two checkmarks read as "answered", which
	// is what a survey response actually is.
	"list-checks": svg`
		<path d="m3 17 2 2 4-4"></path>
		<path d="m3 7 2 2 4-4"></path>
		<path d="M13 6h8"></path>
		<path d="M13 12h8"></path>
		<path d="M13 18h8"></path>`,
	// Email Studio v2 — the image_text block (lucide "panel-left").
	"panel-left": svg`
		<rect width="18" height="18" x="3" y="3" rx="2"></rect>
		<path d="M9 3v18"></path>`,
	// Measure corkscrew — the locked-door affordance (lucide "lock"). Never an
	// emoji per CLAUDE.md; a locked door row/tile states its reason next to
	// this glyph, it is never a dead click.
	lock: svg`
		<rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
		<path d="M7 11V7a5 5 0 0 1 10 0v4"></path>`,
	// Journey canvas vertical redesign — the "Fit whole journey" zoom
	// control (lucide "maximize").
	maximize: svg`
		<path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
		<path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
		<path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
		<path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>`,
	// Workflow canvas (W5): the glyph names the node-type registry sends
	// (backend/internal/workflow/nodes) plus "circle", the canvas's fallback.
	circle: svg`
		<circle cx="12" cy="12" r="10"></circle>`,
	play: svg`
		<polygon points="6 3 20 12 6 21 6 3"></polygon>`,
	bot: svg`
		<path d="M12 8V4H8"></path>
		<rect width="16" height="12" x="4" y="8" rx="2"></rect>
		<path d="M2 14h2"></path>
		<path d="M20 14h2"></path>
		<path d="M15 13v2"></path>
		<path d="M9 13v2"></path>`,
	split: svg`
		<path d="M16 3h5v5"></path>
		<path d="M8 3H3v5"></path>
		<path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"></path>
		<path d="m15 9 6-6"></path>`,
	target: svg`
		<circle cx="12" cy="12" r="10"></circle>
		<circle cx="12" cy="12" r="6"></circle>
		<circle cx="12" cy="12" r="2"></circle>`,
	workflow: svg`
		<rect width="8" height="8" x="3" y="3" rx="2"></rect>
		<path d="M7 11v4a2 2 0 0 0 2 2h4"></path>
		<rect width="8" height="8" x="13" y="13" rx="2"></rect>`,
	"git-fork": svg`
		<circle cx="12" cy="18" r="3"></circle>
		<circle cx="6" cy="6" r="3"></circle>
		<circle cx="18" cy="6" r="3"></circle>
		<path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9"></path>
		<path d="M12 12v3"></path>`,
	"git-merge": svg`
		<circle cx="18" cy="18" r="3"></circle>
		<circle cx="6" cy="6" r="3"></circle>
		<path d="M6 21V9a9 9 0 0 0 9 9"></path>`,
	wrench: svg`
		<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>`,
	radio: svg`
		<path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"></path>
		<path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"></path>
		<circle cx="12" cy="12" r="2"></circle>
		<path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"></path>
		<path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"></path>`,
};

/** Every registered glyph name, sorted — the storybook's icon-gallery source. */
export function cosIconNames(): string[] {
	return Object.keys(ICONS).sort();
}

/**
 * A glyph's children as an SVG template, for a consumer that draws inside
 * its own <svg> (the workflow canvas: cos-icon is an HTML element and cannot
 * sit among SVG nodes). Undefined for a name with no glyph.
 */
export function cosIconGlyph(name: string): ReturnType<typeof svg> | undefined {
	return ICONS[name];
}

@customElement("cos-icon")
export class CosIcon extends LitElement {
	@property({ type: String }) name = "";
	@property({ type: Number }) size = 20;
	@property({ type: String }) label = "";

	static styles = css`
		:host {
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}
		svg {
			width: 100%;
			height: 100%;
			stroke: currentColor;
			fill: none;
			stroke-width: 2;
			stroke-linecap: round;
			stroke-linejoin: round;
		}
	`;

	render() {
		const path = ICONS[this.name];
		if (!path) {
			// Unknown icon name — render an empty box so layout doesn't
			// shift. Component-level forward-compat for new names.
			return html`<svg viewBox="0 0 24 24" aria-hidden="true"
				style=${`width:${this.size}px;height:${this.size}px;`}></svg>`;
		}
		return html`
			<svg viewBox="0 0 24 24"
				style=${`width:${this.size}px;height:${this.size}px;`}
				role=${this.label ? "img" : "presentation"}
				aria-label=${this.label || undefined}
				aria-hidden=${this.label ? "false" : "true"}>
				${path}
			</svg>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"cos-icon": CosIcon;
	}
}
